import * as React from 'react';
import { styled } from '@linaria/react';

import KeyBed from './KeyBed';
import KeyboardControls from './KeyboardControls';

interface Props {
  layers: number;
}

function Synthesizer({ layers }: Props) {
  return (
    <Wrapper data-hide-panels={layers < 3}>
      <LeftEdge data-hide-gradient={layers < 10} />
      <Front>
        <TopRow>
          <TopEdge data-hide-gradient={layers < 6} />
          <TopContent>
            <NameplatePanel></NameplatePanel>
            <VerticalDivider data-hide-gradient={layers < 1} />
            <MiddlePanel></MiddlePanel>
            <VerticalDivider data-hide-gradient={layers < 1} />
            <ButtonPanel></ButtonPanel>
          </TopContent>
        </TopRow>
        <HorizontalDivider data-hide-gradient={layers < 2} />
        <BottomRow>
          <KeyControlsWrapper
            data-hide-gradient={layers < 9}
            data-hide-shadow={layers < 4}
          >
            <KeyboardControls layers={layers} />
          </KeyControlsWrapper>
          <KeyBedWrapper data-hide-gradient={layers < 6}>
            <KeyBed
              numberOfKeys={29}
              startingOctave={3}
              layers={layers}
            />
            <LeftKeyboardEdgeShadow
              style={{ opacity: layers < 3 ? 0 : 1 }}
            />
            <RightKeyboardEdgeShadow
              style={{ opacity: layers < 3 ? 0 : 1 }}
            />
          </KeyBedWrapper>
          <KeyboardEdge
            data-hide-gradient={layers < 9}
            data-hide-shadow={layers < 4}
          />
        </BottomRow>
      </Front>
      <RightEdge data-hide-gradient={layers < 10} />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  --row-height: 100px;
  position: relative;
  display: flex;
  width: calc(30rem + 16px * 2);
  margin-inline: auto;
  user-select: none;
  box-shadow:
    0px 1px 2px var(--color-background),
    0px 4px 8px var(--color-background),
    0px 12px 24px var(--color-background);
`;

const Edge = styled.div`
  position: relative;
  z-index: 0;
  width: 12px;
  background: var(--color-background);

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 2px;
    right: 2px;
    bottom: 0px;
    background: linear-gradient(
      180deg,
      hsl(39deg 50% 26%) 0%,
      hsl(39deg 79% 68%) 3.39%,
      hsl(45deg 88% 69%) 5.68%,
      hsl(39deg 60% 44%) 8.1%,
      hsl(39deg 58% 26%) 11.16%,
      hsl(39deg 54% 30%) 15.93%,
      hsl(39deg 52% 53%) 35.67%,
      hsl(45deg 65% 58%) 53.08%,
      hsl(34deg 54% 25%) 54.05%,
      hsl(44deg 72% 73%) 60.44%,
      hsl(46deg 67% 60%) 66.16%,
      hsl(34deg 66% 39%) 95.04%,
      hsl(40deg 71% 76%) 96.24%,
      hsl(35deg 83% 21%) 100%
    );
  }

  &[data-hide-gradient='true'] {
    &::after {
      background: hsl(39deg 40% 50%);
    }
  }
`;
const LeftEdge = styled(Edge)`
  &::after {
    border-radius: 4px 0 0 4px;
  }

  &[data-hide-gradient='true']::after {
    border-radius: 0;
  }
`;
const RightEdge = styled(Edge)`
  &::after {
    border-radius: 0 4px 4px 0;
  }

  &[data-hide-gradient='true']::after {
    border-radius: 0;
  }
`;

const Front = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Panel = styled.div`
  position: relative;
  height: calc(var(--row-height) - 8px);
  outline: 2px solid hsl(275deg 40% 30%);
  outline-offset: -2px;
  border: 3px solid hsl(275deg 40% 80% / 0.4);
  border-radius: 4px;
  background: hsl(275deg 40% 30%);

  ${Wrapper}[data-hide-panels='true'] & {
    outline: none;
    border: none;
  }
`;

const NameplatePanel = styled(Panel)`
  display: flex;
  z-index: 2;
  flex-direction: column;
  justify-content: space-between;
  width: calc(var(--row-height) - 8px);
  padding: 4px;
  isolation: isolate;
`;

const ButtonPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: calc(var(--row-height) + 7px);
`;

const MiddlePanel = styled(Panel)`
  flex: 1;
`;

const TopRow = styled.div`
  position: relative;
  z-index: 2;
  height: var(--row-height);
`;

const TopEdge = styled.div`
  height: 8px;
  background: linear-gradient(
    to top,
    hsl(275deg 20% 10%) 0px 1px,
    hsl(275deg 47% 37%) 1px 2px,
    hsl(275deg 30% 40%) 4px,
    hsl(275deg 30% 20%)
  );

  &[data-hide-gradient='true'] {
    background: hsl(275deg 40% 30%);
  }
`;

const VolumeFaderWrapper = styled.div`
  margin-bottom: 8px;
  margin-inline: 8px;
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  background: hsl(275deg 40% 30%);
`;

const HorizontalDivider = styled.div`
  height: 4px;
  background: linear-gradient(
    to top,
    hsl(275deg 20% 20%),
    hsl(275deg 40% 50%),
    hsl(275deg 10% 10%),
    hsl(275deg 30% 40%),
    hsl(275deg 50% 40%)
  );

  &[data-hide-gradient='true'] {
    background: hsl(275deg 40% 30%);
  }
`;
const VerticalDivider = styled.div`
  width: 4px;
  background: linear-gradient(
    to right,
    hsl(275deg 20% 20%),
    hsl(275deg 40% 50%),
    hsl(275deg 10% 10%),
    hsl(275deg 30% 40%),
    hsl(275deg 50% 40%)
  );

  &[data-hide-gradient='true'] {
    background: hsl(275deg 40% 30%);
  }
`;

const BottomRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  height: var(--row-height);
  background: var(--color-background);
`;

const KeyControlsWrapper = styled.div`
  position: relative;
  width: 4rem;
  background: linear-gradient(
    to bottom,
    hsl(275deg 50% 40%) 0px,
    hsl(275deg 36% 50%) 4px,
    hsl(275deg 30% 30%) 6px,
    hsl(275deg 30% 60%) 20px,
    hsl(275deg 50% 30%) 80%,
    hsl(275deg 50% 30%) 90%,
    hsl(275deg 30% 60%) 92%,
    hsl(275deg 50% 30%) 98%
  );

  &[data-hide-gradient='true'] {
    background: hsl(275deg 40% 30%);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 2px;
    height: calc(100% - 6px);
    background: var(--color-background);
    border-radius: 0 0 0 100px;
  }

  &[data-hide-shadow='true'] {
    &::after {
      opacity: 0;
    }
  }
`;

const KeyBedWrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  /* Clip the shadow that sits along the right edge (KeyboardEdgeShadow) */
  overflow: hidden;
  overflow: clip;

  /* Pseudo-elements used for the edges above/below keyboard */
  &::before {
    content: '';
    height: 6px;
    background: linear-gradient(
      to bottom,
      hsl(275deg 50% 30%),
      hsl(275deg 10% 10%)
    );
  }
  &::after {
    content: '';
    height: 8px;
    border-top: 2px solid var(--color-background);
    background: linear-gradient(
      to bottom,
      hsl(275deg 25% 51%),
      hsl(275deg 46% 30%) 82%
    );
  }

  &[data-hide-gradient='true'] {
    &::before,
    &::after {
      background: hsl(275deg 40% 30%);
    }
  }
`;

const KeyboardEdge = styled.div`
  position: relative;
  width: 1rem;
  background: linear-gradient(
    to bottom,
    hsl(275deg 50% 40%) 0px,
    hsl(275deg 36% 50%) 4px,
    hsl(275deg 30% 30%) 6px,
    hsl(275deg 30% 60%) 20px,
    hsl(275deg 50% 30%) 80%,
    hsl(275deg 50% 30%) 90%,
    hsl(275deg 30% 60%) 92%,
    hsl(275deg 50% 30%) 98%
  );

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 2px;
    height: calc(100% - 6px);
    background: var(--color-background);
    border-radius: 0 0 100px 0;
  }

  &[data-hide-gradient='true'] {
    background: hsl(275deg 40% 30%);
  }

  &[data-hide-shadow='true'] {
    &::after {
      opacity: 0;
    }
  }
`;

const KeyboardEdgeShadow = styled.div`
  position: absolute;
  top: 0;
  bottom: 8px;
  width: 16px;
  filter: blur(3px);
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: hsl(275deg 50% 20% / 0.5);
  }
`;
const LeftKeyboardEdgeShadow = styled(KeyboardEdgeShadow)`
  left: -4px;

  &::after {
    clip-path: polygon(0% 0%, 100% 0%, 60% 30%, 60% 100%, 0% 100%);
  }
`;
const RightKeyboardEdgeShadow = styled(KeyboardEdgeShadow)`
  right: -3px;
  opacity: 0.5;

  &::after {
    clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 60% 100%, 60% 30%);
  }
`;

export default React.memo(Synthesizer);
